import type { AreaId, PermissionRequestId, PersonId, SubAreaId } from '@shared/brand';
import type { CalendarDate } from '../value-objects/CalendarDate';
import type { DateRange } from '../value-objects/DateRange';
import type { PermissionRequestStatus } from '../vocabulary';
import type { Auditable } from './Organization';

/**
 * Home office, a personal day, or a few hours off — the handoff's "permisos".
 *
 * Distinct from a vacation request rather than a variant of it, because the
 * rules genuinely differ: these do not consume the vacation balance, they may be
 * measured in hours instead of days, they may be granted with or without pay,
 * and they need one approval rather than two. Folding them into
 * `VacationRequest` with a discriminator would put four nullable columns on the
 * central table and make every balance query filter on type.
 */
export interface PermissionRequest extends Auditable {
  readonly id: PermissionRequestId;
  /** e.g. `PER-0781`. */
  readonly folio: string;
  readonly personId: PersonId;
  readonly personName: string;
  readonly areaId: AreaId;
  readonly subAreaId: SubAreaId | null;
  readonly subAreaName: string;
  /**
   * The kind's name, copied from the catalogue at creation rather than
   * referenced. The catalogue is runtime-editable: renaming "Home office" must
   * not rewrite the history of every permission already granted under that
   * name.
   */
  readonly kind: string;
  readonly range: DateRange;
  /** Set only for an hours-based kind; `null` for a whole-day permission. */
  readonly hours: HourWindow | null;
  readonly withPay: boolean;
  readonly reason: string;
  readonly status: PermissionRequestStatus;
  readonly decidedByPersonId: PersonId | null;
  readonly decidedByName: string;
  readonly decidedAt: Date | null;
  readonly resolutionReason: string | null;
}

/**
 * Cuánto tiempo del día se pide, en una de dos formas.
 *
 * **Fijada al reloj** —«Permiso de horas»—: `from` y `to`, ambos `HH:mm`. Sale a
 * las nueve y vuelve a la una.
 *
 * **Por duración** —«Salir temprano»—: sólo `earlyHours`. Se pide salir dos horas
 * antes, y a qué hora es eso depende de la jornada de cada quien, que el portal no
 * tiene configurada en ningún sitio. Guardar un `16:00` calculado sería inventarse
 * ese dato; guardar «dos horas» es exactamente lo que la persona pidió y lo que su
 * líder autoriza.
 *
 * Hay una tercera forma que **sólo se lee, ya no se escribe**: `from` con `to`
 * nulo, que es como se guardaron las salidas anticipadas el 18 de agosto de 2026,
 * entre que se separó «Salir temprano» y que se cambió a preguntar por duración.
 * Los documentos existentes siguen siendo legibles.
 */
export interface HourWindow {
  /** Hora de salida. Nula cuando el permiso se pidió por duración. */
  readonly from: string | null;
  /** Hora de regreso. Nula cuando no se regresa ese día. */
  readonly to: string | null;
  /** Horas que se pide salir antes. Sólo en el permiso sin regreso. */
  readonly earlyHours: number | null;
}

const HOUR_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const isHourString = (value: string): boolean => HOUR_PATTERN.test(value);

/**
 * Minutes covered by an hour window; 0 when it does not describe a duration.
 *
 * Una salida por duración sí se puede contar —son sus horas—, y eso es lo que
 * cambió al preguntarlas con un desplegable en vez de deducirlas de un reloj que
 * no teníamos. Sigue dando 0 la forma antigua, la de `from` sin `to`: ahí no hay
 * fin de jornada del que restar, y quien la muestre tiene que preguntar por la
 * forma antes de leer el cero como «cero horas».
 */
export const hourWindowMinutes = (window: HourWindow): number => {
  if (window.earlyHours !== null) return Math.max(0, window.earlyHours) * 60;
  if (window.from === null || window.to === null) return 0;
  const from = HOUR_PATTERN.exec(window.from);
  const to = HOUR_PATTERN.exec(window.to);
  if (from === null || to === null) return 0;
  const start = Number(from[1]) * 60 + Number(from[2]);
  const end = Number(to[1]) * 60 + Number(to[2]);
  return Math.max(0, end - start);
};

/**
 * El tope de un permiso de horas, y de una salida anticipada con él.
 *
 * Cuatro horas es media jornada: más que eso ya es un día personal, y así lo dice
 * el aviso del asistente desde que existe. Vive aquí y no en la pantalla porque
 * ahora también gobierna las opciones del desplegable, y dos topes que se
 * desalineen serían un desplegable ofreciendo lo que el aviso reprocha.
 */
export const MAX_HOUR_PERMIT_HOURS = 4;

/**
 * A kind of permission, as configured at runtime. `Catalogs` holds these; the
 * shape is here because the domain reasons about `measuredInHours` when
 * validating a request.
 */
export interface PermissionKind {
  readonly id: string;
  readonly note: string;
  /** Unicode glyph from the handoff — the design ships no icon library. */
  readonly icon: string;
  readonly measuredInHours: boolean;
  /**
   * Si se va y no vuelve ese día.
   *
   * Sólo tiene sentido con `measuredInHours`: es lo que distingue «Salir
   * temprano» de «Permiso de horas», que sale y regresa. Es una bandera del
   * catálogo y no un nombre reconocido en el código a propósito — RR.HH. puede
   * dar de alta «Salida médica» mañana sin que nadie despliegue nada, y el
   * formulario sabrá que no debe pedir hora de vuelta.
   */
  readonly openEnded: boolean;
  /**
   * Si se puede pedir para días que ya pasaron.
   *
   * Apagada, el permiso se pide de hoy en adelante, que es lo normal: pedir home
   * office para ayer no es pedir permiso, es reportar. Hoy sí entra —salir
   * temprano se pide el día en que uno se va—, y el servidor lo comprueba igual.
   *
   * Encendida para los dos tipos que **por definición se registran después de que
   * ocurrió la cosa**. La paternidad la da la LFT desde el nacimiento, y exigir que
   * se pida antes es pedir que se avise del parto con antelación. El fallecimiento
   * de un familiar se registra al volver: la persona se fue el día que pasó, no
   * cuando pudo abrir el portal.
   *
   * Es del catálogo y no una lista de nombres en el código, por lo mismo que
   * `openEnded`: RR.HH. puede dar de alta «Permiso por matrimonio» y decidir sin
   * que nadie despliegue.
   */
  readonly retroactive: boolean;
  readonly withPay: boolean;
  readonly enabled: boolean;
}

export const DEFAULT_PERMISSION_KINDS: readonly PermissionKind[] = [
  {
    id: 'Home office',
    note: 'Hasta 2 días por semana',
    icon: '⌂',
    measuredInHours: false,
    openEnded: false,
    retroactive: false,
    withPay: true,
    enabled: true,
  },
  {
    id: 'Día personal',
    note: '4 al año, con goce',
    icon: '☺',
    measuredInHours: false,
    openEnded: false,
    retroactive: false,
    withPay: true,
    enabled: true,
  },
  {
    id: 'Permiso de horas',
    note: 'Hasta 4 h, mismo día',
    icon: '◕',
    measuredInHours: true,
    openEnded: false,
    retroactive: false,
    withPay: true,
    enabled: true,
  },
  {
    id: 'Permiso sin goce',
    note: 'Autoriza RR.HH.',
    icon: '⊘',
    measuredInHours: false,
    openEnded: false,
    retroactive: false,
    withPay: false,
    enabled: true,
  },
  /*
   * Fallecimiento de un familiar.
   *
   * **La LFT no lo obliga**, y conviene decirlo en vez de inventarle un artículo:
   * no hay licencia por duelo en la ley federal. Lo que existe son contratos
   * colectivos y políticas internas que suelen dar entre uno y tres días.
   *
   * Por eso la nota no cita ley: dice que lo fija la empresa. Cuando RR.HH.
   * decida los días, se editan desde Configuración global › Catálogos sin
   * desplegar nada.
   */
  {
    id: 'Fallecimiento de familiar',
    note: 'Días según la política de la empresa · la LFT no lo obliga',
    icon: '🕊',
    measuredInHours: false,
    openEnded: false,
    retroactive: true,
    withPay: true,
    enabled: true,
  },
  /*
   * Salir temprano. Se mide en horas, como su hermano «Permiso de horas»: la
   * diferencia es que éste no vuelve, y contar eso como un día completo le
   * descontaría de más a quien sólo se fue dos horas antes.
   */
  {
    id: 'Salir temprano',
    note: 'Se va antes y no regresa ese día',
    icon: '↘',
    measuredInHours: true,
    openEnded: true,
    retroactive: false,
    withPay: true,
    enabled: true,
  },
];

export const isPermissionDecided = (status: PermissionRequestStatus): boolean =>
  status === 'Aprobado' || status === 'Rechazado' || status === 'Cancelado';

/**
 * Whether the person who filed a permit can still withdraw it.
 *
 * Two conditions, and the second is the one worth spelling out: a permit that
 * has already begun is no longer a plan, it is the record of a day somebody was
 * out. Cancelling it then would erase the reason a desk was empty, so the button
 * disappears the morning the permit starts rather than the day after it ends.
 *
 * Mirrors `cancelPermissionRequest` exactly — the server is what decides, and a
 * button offering something the server will refuse is worse than no button.
 */
export const isPermissionCancelable = (
  permission: Pick<PermissionRequest, 'status' | 'range'>,
  today: CalendarDate,
): boolean =>
  (permission.status === 'Por aprobar' || permission.status === 'Aprobado') &&
  permission.range.start.isAfter(today);
